import { useState, useEffect } from "react";

/**
 * Removes the background from a logo using edge flood-fill.
 * Seeds from every border pixel, spreading to connected pixels that are
 * "similar enough" to the seed colour. Returns a transparent-background PNG.
 */
export function useTransparentLogo(src: string): string | null {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!src) return;
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) { setDataUrl(src); return; }

      ctx.drawImage(img, 0, 0);

      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imageData.data;
        const w = canvas.width;
        const h = canvas.height;

        // Colour distance in RGB space (0-1 per channel)
        const colorDist = (i: number, r: number, g: number, b: number) =>
          Math.sqrt(
            Math.pow(d[i] / 255 - r, 2) +
            Math.pow(d[i + 1] / 255 - g, 2) +
            Math.pow(d[i + 2] / 255 - b, 2)
          );

        // Flood-fill from edges with a given seed colour + tolerance
        const floodFill = (seedR: number, seedG: number, seedB: number, tolerance: number) => {
          const visited = new Uint8Array(w * h);
          const queue: number[] = [];

          const enqueue = (x: number, y: number) => {
            const idx = y * w + x;
            if (visited[idx]) return;
            const pi = idx * 4;
            if (d[pi + 3] === 0) return; // already transparent
            if (colorDist(pi, seedR, seedG, seedB) <= tolerance) {
              visited[idx] = 1;
              queue.push(x, y);
            }
          };

          // Seed all border pixels
          for (let x = 0; x < w; x++) { enqueue(x, 0); enqueue(x, h - 1); }
          for (let y = 1; y < h - 1; y++) { enqueue(0, y); enqueue(w - 1, y); }

          const dx = [1, -1, 0, 0];
          const dy = [0, 0, 1, -1];

          let head = 0;
          while (head < queue.length) {
            const cx = queue[head++];
            const cy = queue[head++];
            // Make transparent with smooth falloff near edges
            const pi = (cy * w + cx) * 4;
            const dist = colorDist(pi, seedR, seedG, seedB);
            const alpha = Math.max(0, (dist / tolerance) - 0.5) * 2; // 0 near seed, 1 far
            d[pi + 3] = Math.round(d[pi + 3] * alpha);

            for (let n = 0; n < 4; n++) {
              enqueue(cx + dx[n], cy + dy[n]);
            }
          }
        };

        // Sample the top-left corner as the background seed colour
        const seedR = d[0] / 255;
        const seedG = d[1] / 255;
        const seedB = d[2] / 255;

        floodFill(seedR, seedG, seedB, 0.35);

        // Second pass: remove any remaining near-white pixels (enclosed letter counters)
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] === 0) continue; // already transparent
          const r = d[i] / 255;
          const g = d[i + 1] / 255;
          const b = d[i + 2] / 255;
          const whiteness = Math.min(r, g, b);
          if (whiteness > 0.78) {
            const alpha = 1 - (whiteness - 0.78) / 0.22;
            d[i + 3] = Math.round(d[i + 3] * Math.max(0, alpha));
          }
        }

        ctx.putImageData(imageData, 0, 0);
        setDataUrl(canvas.toDataURL("image/png"));
      } catch {
        setDataUrl(src);
      }
    };

    img.onerror = () => setDataUrl(src);
    img.src = src;
  }, [src]);

  return dataUrl;
}

